import React, { useState } from "react";
// Removed antd dependency
import Code from "../../code";
import { ExecutionResults, Implementation } from "./types";
import {
  Github,
  CheckCircle,
  XCircle,
  Clock,
  CodeXml,
  ExternalLink,
} from "lucide-react";
import { getFrameworkInfo } from "./detail";
import { TruncatableText } from "@multiagentbook/ui";

interface CodeViewProps {
  implementation: Implementation;
  baseUrl: string;
}

export const CodeViewer: React.FC<CodeViewProps> = ({
  implementation,
  baseUrl,
}) => {
  const [view, setView] = useState<"code" | "results">("code");
  const [content, setContent] = useState<{
    code: string;
    results: ExecutionResults | null;
  }>({ code: "", results: null });
  const [loading, setLoading] = useState(true);

  const frameworkDetails = getFrameworkInfo(implementation.framework);

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  React.useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const [codeRes, resultsRes] = await Promise.all([
          fetch(`${baseUrl}${implementation.code.path}`),
          implementation.results
            ? fetch(`${baseUrl}${implementation.results.path}`)
            : Promise.resolve(null),
        ]);

        const code = await codeRes.text();
        const results = resultsRes ? await resultsRes.json() : null;

        setContent({ code, results });
      } catch (error) {
        console.error("Error fetching content:", error);
      }
      setLoading(false);
    };

    fetchContent();
  }, [implementation, baseUrl]);

  if (loading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        Loading...
      </div>
    );
  }

  const renderStatusLine = () => {
    if (view === "code") {
      return (
        <div className="flex mt-4 items-center gap-2 text-sm text-secondary">
          {implementation.code.loc && (
            <span>
              <CodeXml className="w-4 h-4 mr-2 inline-block" />
              {implementation.code.loc} lines of code
            </span>
          )}
        </div>
      );
    }

    if (!content.results) return null;

    const { execution } = content.results;
    const timeAgo = getTimeAgo(execution.start_time);

    return (
      <div className="flex mt-4 text-secondary  items-center gap-2 text-sm">
        {execution.timed_out ? (
          <Clock className="w-4 h-4 text-orange-500" />
        ) : execution.success ? (
          <CheckCircle className="w-4 h-4 text-green-500" />
        ) : (
          <XCircle className="w-4 h-4 text-red-500" />
        )}
        <span>
          {execution.duration_seconds.toFixed(2)}s | Last run {timeAgo}
        </span>
        {execution.error && (
          <span className="text-red-500 ml-2">{execution.error}</span>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!content.results) return "No results available";

    const { output } = content.results;

    return (
      <div className="space-y-4">
        {output.stdout && (
          <pre className="font-mono text-sm whitespace-pre-wrap overflow-auto max-h-[500px] p-4 bg-tertiary rounded">
            {output.stdout}
          </pre>
        )}

        {output.stderr && (
          <pre className="font-mono text-sm whitespace-pre-wrap overflow-auto max-h-[500px] p-4 bg-red-50 text-red-800 rounded">
            {output.stderr}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4 pt-2 sticky top-0 bg-primary pb-2 z-10">
        <div className="flex   mb-2">
          <div className="flex flex-1 items-center gap-4">
            <div className="flex rounded-md border border-secondary bg-primary p-1">
              <button
                onClick={() => setView("code")}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  view === "code"
                    ? "bg-accent text-white"
                    : "text-primary hover:bg-secondary"
                }`}
              >
                Code
              </button>
              <button
                onClick={() => setView("results")}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  view === "results"
                    ? "bg-accent text-white"
                    : "text-primary hover:bg-secondary"
                }`}
              >
                View results
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm text-primary">
              <frameworkDetails.icon className="w-4 h-4" />
              <span>{frameworkDetails.name}</span>
            </div>
          </div>

          {implementation.code.githubUrl && (
            <a
              href={implementation.code.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex mr-2  items-center gap-2 text-sm text-primary hover:text-accent"
            >
              <Github className="w-4 h-4" />
              View Source
            </a>
          )}
          {implementation.code.metadata?.blog && (
            <a
              href={implementation.code.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:text-accent"
            >
              <ExternalLink className="w-4 h-4" />
              Blog
            </a>
          )}
        </div>

        <div className="border-b pb-2 text-">{renderStatusLine()}</div>

        {implementation.code.metadata &&
          implementation.code.metadata?.description && (
            <div className="rounded py-2 text-xs text-secondary">
              <TruncatableText
                content={implementation.code.metadata?.description}
                textThreshold={500}
              />
            </div>
          )}
      </div>

      {view === "code" ? (
        <Code>
          {{
            props: {
              className: `language-${implementation.code.language}`,
              children: content.code,
              file: implementation.code.path.split("/").pop(),
            },
          }}
        </Code>
      ) : (
        renderResults()
      )}
    </div>
  );
};

export default CodeViewer;
